import React, { useCallback, useEffect, useState } from "react";
import {
  getStaffAnalytics,
  getStaffAnalyticsPortal,
} from "../../api/staff/staffAnalyticsApi";
import KPIGroup from "./KPIGroup";
import ChartCard from "./ChartCard";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import LineChart from "./LineChart";
import { useToast } from "../../context/ToastContext";

const PERIOD_OPTIONS = [
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const LoadingState = () => (
  <div className="flex h-40 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center">
    <p className="text-sm text-rose-700">{message || "Failed to load analytics"}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="rounded-md bg-rose-600 px-3 py-1 text-xs text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

/**
 * Staff + Role analytics — minimal set only.
 * isPortal controls which API endpoint is called.
 */
const StaffAnalytics = ({ isPortal = false }) => {
  const [period, setPeriod] = useState("year");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = isPortal
        ? await getStaffAnalyticsPortal(period)
        : await getStaffAnalytics(period);
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [period, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};
  const growth = data?.growth || {};

  const growthSub =
    growth.direction === "up"
      ? `↑ ${growth.staffGrowthPercent}%`
      : growth.direction === "down"
      ? `↓ ${Math.abs(growth.staffGrowthPercent)}%`
      : null;

  return (
    <div className="space-y-3">
      {/* Header + period filter */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-neutral-200">Staff Overview</h2>
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${ period === opt.value ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-200" }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <LoadingState />}

      {!loading && error && (
        <ErrorState message={error} onRetry={fetchData} />
      )}

      {!loading && !error && data && (
        <>
          {/* Grouped KPIs — 3 cards max */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPIGroup
              title="Staff Strength"
              items={[
                { label: "Total", value: kpis.totalStaff },
                { label: "Active", value: kpis.activeStaff, tone: "success" },
                { label: "Inactive", value: kpis.inactiveStaff, tone: "muted" },
                {
                  label: "New",
                  value: kpis.newJoiners,
                  sub: growthSub,
                  tone:
                    growth.direction === "up"
                      ? "success"
                      : growth.direction === "down"
                      ? "danger"
                      : "default",
                },
              ]}
            />
            <KPIGroup
              title="Employment"
              items={[
                { label: "Full-time", value: kpis.fullTime },
                { label: "Part-time", value: kpis.partTime },
                { label: "Contract", value: kpis.contract },
                { label: "On leave", value: kpis.onLeave, tone: "warning" },
              ]}
            />
            <KPIGroup
              title="Roles"
              items={[
                { label: "Total roles", value: kpis.totalRoles },
                { label: "Active", value: kpis.activeRoles, tone: "success" },
                { label: "Male", value: kpis.male },
                { label: "Female", value: kpis.female },
              ]}
            />
          </div>

          {/* Charts — only the most useful ones */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="By Role">
              <BarChart
                data={distributions.byRole || []}
                layout="horizontal"
                height={200}
                color="#4F46E5"
                categoryWidth={110}
              />
            </ChartCard>

            <ChartCard title="Employment Type">
              <DonutChart
                data={distributions.byEmploymentType || []}
                height={200}
              />
            </ChartCard>
          </div>

          <ChartCard title="Joining Trend">
            <LineChart
              data={trends.joiningTrend || []}
              height={180}
              dataKey="count"
              xKey="period"
            />
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default StaffAnalytics;
